import { Buffer } from "buffer";
import { UploadedFile } from "../models/file.models";
import XMLBuilder from "fast-xml-builder";
import { XMLParser } from "fast-xml-parser";
import { FileFieldAttachment } from "../models/imis.models";

/** Static-method namespace for converting, downloading, and packaging file content for use in iMIS file fields and browser downloads. */
export class FilesUtility {
    /**
     * Encodes binary/string content as a base64 string.
     * @param content The content to encode.
     * @returns The base64-encoded representation of the content.
     */
    public static convertBinaryToBase64(content: string): string {
        return Buffer.from(content).toString("base64");
    }

    /**
     * Builds a browser object URL (blob URL) for the given content, which can be used as a download or embed source.
     * @param content The raw content/data for the file, treated as a sequence of character codes to build the underlying byte array.
     * @param contentType The MIME content type to assign to the resulting blob.
     * @returns An object URL referencing a `Blob` created from the content.
     */
    public static createObjectUrl(content: string, contentType: string): string {
        let byteNumbers = new Array(content.length);
        for (let i = 0; i < content.length; i++) {
            byteNumbers[i] = content.charCodeAt(i);
        }

        let byteArray = new Uint8Array(byteNumbers);
        let blob = new Blob([byteArray], { type: contentType });

        return URL.createObjectURL(blob);
    }

    /**
     * Triggers a browser download of the given content as a file, by creating a temporary object URL, downloading it, then revoking it.
     * @param content The raw content/data to download.
     * @param contentType The MIME content type of the file being downloaded.
     * @param fileName The file name to give the downloaded file.
     */
    public static downloadContentAsFile(content: string, contentType: string, fileName: string) {
        let dataObjectUrl = this.createObjectUrl(content, contentType);
        this.downloadUrlAsFile(dataObjectUrl, fileName);
        
        URL.revokeObjectURL(dataObjectUrl);
    }

    /**
     * Triggers a browser download of a URL's content as a file, by creating a hidden anchor with a `download` attribute and programmatically clicking it.
     * @param url The URL of the content to download.
     * @param fileName The file name to give the downloaded file.
     */
    public static downloadUrlAsFile(url: string, fileName: string) {
        let jLink = jQuery("<a style='display: none;'></a>").attr({
            href: url,
            download: fileName
        });

        jQuery("body").append(jLink);
        jLink[0].click();
        jLink.remove();
    }

    /**
     * Builds the base64-encoded, pretty-printed XML string expected by an iMIS "file" field, wrapping the file's content type, type extension, name, and data.
     * @param fileName The name of the file being uploaded, used to derive its content type and type extension.
     * @param fileData The file's raw content/data.
     * @returns A base64-encoded XML string suitable for use as an iMIS file field value.
     */
    public static getFileDataStringForFileField(fileName: string, fileData: string): string {
        // convert the file into a parsed file xml object
        // return that xml object as base64 string to be used in the file field of the payload
        // we have to pretty print the xml because otherwise imis throws an error when trying to read the file field
        let uploadedFile = new UploadedFile(fileName, fileData);
        let fileContentType = this.convertBinaryToBase64(uploadedFile.fileContentType);
        let fileType = this.convertBinaryToBase64(uploadedFile.fileType);
        let uploadedFileName = this.convertBinaryToBase64(uploadedFile.uploadedFileName);
        
        let xmlFileObject = {
            UploadedFile: {
                FileContentType: fileContentType,
                FileType: fileType,
                UploadedFileName: uploadedFileName,
                File: uploadedFile.file
            }
        };
        let xmlBuilder = new XMLBuilder({
            format: true,
            indentBy: "  ",
        });
        let xmlString = xmlBuilder.build(xmlFileObject);
        return this.convertBinaryToBase64(xmlString);
    }

    /**
     * Converts provided base64 string into an UploadedFile object
     * @param fileFieldDataString the base64 string returned for file fields
     * @returns {UploadedFile}
     */
    public static getFileDataFromFileFieldDataString(fileFieldDataString: string): UploadedFile {
        let fileData: UploadedFile;
        
        // convert the base64 string into a parsed xml object
        // get the file name and base64 file data string from the xml object
        // return the name and data string
        const parser = new XMLParser();
        let xmlFileObject: FileFieldAttachment = parser.parse(Buffer.from(fileFieldDataString, 'base64').toString());
        let uploadedFileName = Buffer.from(xmlFileObject.UploadedFile.UploadedFileName, 'base64').toString();
        // ensures file data stays in binary format
        let fileDataString = Buffer.from(xmlFileObject.UploadedFile.File, 'base64').toString("binary");
        
        fileData = new UploadedFile(uploadedFileName, fileDataString);
        return fileData;
    }
}